import { type ConfluenceCreateDatabaseCommandProperties } from '@atlassian/rovo-content-bridge-api-commands';
import { logError } from '@databases/logs';

import { getConfluenceService } from '@canvas/atlassian-service/confluence';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import {
  AI_FIELD_STREAM_ACTIVE_ERROR_MESSAGE,
  DatabaseStreamState
} from '../database-stream-state.ts';
import { sendDatabaseAIFCExperienceTrackingToParent } from '../metrics/rovo-bridge-database-metrics.ts';

const streamState = DatabaseStreamState.getInstance();

const logDatabaseTitlePersistenceError = (possibleError: unknown) => {
  const error =
    possibleError instanceof Error ? possibleError : new Error(String(possibleError));
  logError('Error updating native database title', error);
};

const persistDatabaseTitle = (contentId: string, title: string) => {
  if (!title) {
    return;
  }

  try {
    getConfluenceService()
      .updateContentTitle(contentId, title)
      .catch((possibleError) => {
        return logDatabaseTitlePersistenceError(possibleError);
      });
  } catch (possibleError) {
    return logDatabaseTitlePersistenceError(possibleError);
  }
};

declare global {
  interface Window {
    isPreview?: boolean;
  }
}

export const createDatabase = async (
  params: ConfluenceCreateDatabaseCommandProperties,
  contentId: string
) => {
  try {
    if (streamState.isAiFieldStreamingActive) {
      throw new Error(AI_FIELD_STREAM_ACTIVE_ERROR_MESSAGE);
    }

    if (
      !streamState.isStreamingActive &&
      (!params.isStreamComplete || params.data.document.trim().length > 0)
    ) {
      sendDatabaseAIFCExperienceTrackingToParent({
        status: 'success',
        experienceName: 'aiMateAifcInitialDataReceived',
        contentId
      });
    }

    if (params.isStreamComplete && !(globalThis as any).isPreview) {
      persistDatabaseTitle(contentId, params.data.title);
      if (isFeatureFlagEnabled('cc-dbs-hot-307369-fix')) {
        await streamState.ingestCompletionDocumentIfEmpty(
          contentId,
          params.groupId,
          params.data.document
        );
      }
      await streamState.completeStreaming();
      streamState.scheduleActionedEvaluation(
        contentId,
        'create',
        params.singleInstrumentationID ?? ''
      );

      sendDatabaseAIFCExperienceTrackingToParent({
        status: 'success',
        experienceName: 'aiMateAifcAllDataHandled',
        contentId
      });
      streamState.markAllDataHandled();

      return;
    }

    if (!streamState.isStreamingActive) {
      streamState.startStreaming(contentId, params.groupId, {
        shouldClearContent: true
      });
    }

    streamState.processChunk(params.data.document);
  } catch (possibleError) {
    const error =
      possibleError instanceof Error ? possibleError : new Error(String(possibleError));
    sendDatabaseAIFCExperienceTrackingToParent({
      status: 'fail',
      error,
      experienceName: 'aiMateAifcAllDataHandled',
      contentId
    });
    throw error;
  }
};
